import { css, html, LitElement } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import { choose } from "lit/directives/choose.js";
import { container } from "tsyringe";
import { htmlTitle } from "se-shared/directives/html-title.directive";
import { Router } from "@vaadin/router";
import { AgentTasksViewModel } from "../../models/agent-tasks-view-model";
import { AuthService } from "../../services/auth.service";
import { RunService } from "../../services/run.service";
import { ToasterService } from "se-shared/services/toaster.service";
import { DataGridColumn } from "../components/data-grid-template";
import { SeDataGrid } from "../components/data-grid.element";
import { UserState } from "../../services/user.state";
import { MenuItem } from "../components/menu.element";
import { CheckboxEditorElement } from "../editors/checkbox-editor.element";
import { InputEditorElement } from "../editors/input-editor.element";
import { MultiSelectEditor } from "../editors/multi-select-editor.element";
import { SePaginationElement } from "../components/pagination.element";
import { SortOrder } from "se-shared/enums/sort-order";
import { MenuService } from "../../services/menu.service";
import { ModalDialogService } from "../../services/modal-editor.service";
import { RunStatus, isRunningOnServer } from "../../enums/run-status";
import "../components/status.element";

@customElement("se-org-schedules")
export class SeOrgSchedulesElement extends LitElement {
    private _authService: AuthService;
    private _runService: RunService;
    private _toasterService: ToasterService;
    private _userState: UserState;
    private _menuService: MenuService;
    private _modalService: ModalDialogService;
    private _columns: DataGridColumn[] = [];
    
    private _pageIndex = 1;
    private _recordsPerPage = 15;
    private _totalRecordCount: number;
    private _sortColumn = "nextRunTime";
    private _sortOrder = 0;
    private _searchTerm = "null";
    private _searchDebounceTimer: number | undefined;
    private filters: {
        status?: string;
        agentName?: string;
        agentIds?: string;
    } = {};
    
    @state() private _data?: AgentTasksViewModel[];
    @state() private _isLoading = true;
    @state() private _hasSelectedRows = false;

    private _paginationInitialized = false;
    private _sortInitialized = false;
    private _agentNames: { id: number; name: string }[] = [];

    @query("se-data-grid") private _dataGrid: SeDataGrid;
    @query("#selectAll") private _selectAll: CheckboxEditorElement;
    @query("#filter") private _filterEditor: InputEditorElement;
    @query("#multiSelectStatus") private _multiSelectStatus: MultiSelectEditor;
    @query("#multiSelectAgentName") private _multiSelectAgentName: MultiSelectEditor;
    @query("se-pagination") private _pagination: SePaginationElement;

    constructor() {
        super();
        this._authService = container.resolve(AuthService);
        this._runService = container.resolve(RunService);
        this._toasterService = container.resolve(ToasterService);
        this._userState = container.resolve(UserState);
        this._menuService = container.resolve(MenuService);
        this._modalService = container.resolve(ModalDialogService);
    }

    public onBeforeEnter() {
        if (!this._authService.isOrgAdmin) {
            return Router.go("/login");
        }
    }

    connectedCallback() {
        super.connectedCallback();
        
        // Listen for URL initialization events from pagination and data grid
        this.addEventListener('url-initialized', this._handlePaginationInitialized);
        this.addEventListener('sort-initialized', this._handleSortInitialized);
        
        document.title = "Scheduled Organization Runs"
        this._userState.selectedSpaceId = -8;
        this._userState.selectedLabelId = -1;
        this._userState.selectedSpaceOrLabelChanged.triggerVoid();
        this.loadDataAsync();
    }

    disconnectedCallback() {
        super.disconnectedCallback();

        if (this._searchDebounceTimer) {
            clearTimeout(this._searchDebounceTimer);
        }
        
        this.removeEventListener('url-initialized', this._handlePaginationInitialized);
        this.removeEventListener('sort-initialized', this._handleSortInitialized);
    }

    private _handlePaginationInitialized = (event: CustomEvent) => {
        this._paginationInitialized = true;
        
        // Update page index from the message
        if (event.detail && event.detail.pageIndex) {
            this._pageIndex = event.detail.pageIndex;
        }
        
        this._tryLoadData();
    }
    
    private _handleSortInitialized = (event: CustomEvent) => {
        this._sortInitialized = true;
        
        // Update local sort variables from the message
        if (event.detail) {
            const { sortColumn, sortOrder } = event.detail;
            if (sortColumn) {
                this._sortColumn = sortColumn;
                this._sortOrder = sortOrder === SortOrder.Ascending ? 1 : 0;
            }
        }
        
        this._tryLoadData();
    }
    
    private _tryLoadData() {
        // Load data when both components have initialized
        if (this._paginationInitialized && this._sortInitialized) {
            this.loadDataAsync();
        }
    }

    private formatDateTime(date?: Date): string {
        if (!date) return "N/A";
        const d = new Date(date);
        return d.toLocaleString(undefined, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    }

    private getStatus(color: string, text: string, row: AgentTasksViewModel) {
        if (row.message && !isRunningOnServer(row.status)) {
            return html`<se-status
                ${htmlTitle(row.message, true)}
                status-message="${text}"
                status-color="${color}"
                style="width: 100%; max-width: 80px"
                dotted
            ></se-status>`;
        } else {
            return html`<se-status style="width: 100%; max-width: 80px" status-message="${text}" status-color="${color}"></se-status>`;
        }
    }

    private async loadDataAsync() {
        this._columns = [
            { 
                field: "configName", 
                title: "Agent",
                sortable: true,
                template: (row) => html`<a href="javascript:;" style="text-decoration: none;" @click=${() => this.openAgent(row)} @mouseover=${(e) => e.target.style.textDecoration = "underline"} @mouseout=${(e) => e.target.style.textDecoration = "none"}>${row.configName}</a>`
            },
            { 
                field: "name", 
                title: "Schedule",
                sortable: true,
                template: (row) => html`<a href="javascript:;" style="text-decoration: none;" @click=${() => this.openSchedule(row)} @mouseover=${(e) => e.target.style.textDecoration = "underline"} @mouseout=${(e) => e.target.style.textDecoration = "none"}>${row.name}</a>`
            },
            {
                field: "spaceName",
                title: "Space",
                sortable: true,
                template: (row) => html`<a href="javascript:;" style="text-decoration: none;" @click=${() => this.openSpace(row)} @mouseover=${(e) => e.target.style.textDecoration = "underline"} @mouseout=${(e) => e.target.style.textDecoration = "none"}>${row.spaceName}</a>`
            },
            {
                field: "nextRunTime",
                title: "Next Run",
                sortable: true,
                cellStyle: { whiteSpace: "nowrap", minWidth: "150px" },
                template: (row) => html`${this.formatDateTime(row.nextRunTime)}`
            },
            {
                field: "startTime",
                title: "Last Run",
                sortable: true,
                cellStyle: { whiteSpace: "nowrap", minWidth: "150px" },
                template: (row) => {
                    if (!row.startTime) return html`N/A`;
                    return html`<a href="javascript:;" style="text-decoration: none;" @click=${() => this.openRunDetails(row)} @mouseover=${(e) => e.target.style.textDecoration = "underline"} @mouseout=${(e) => e.target.style.textDecoration = "none"}>
                        ${this.formatDateTime(row.startTime)}
                    </a>`;
                }
            },
            {
                field: "status",
                title: "Status",
                sortable: true,
                align: "center",
                template: (row) =>
                    html`${choose(
                        row.status,
                        [
                            [undefined, () => this.getStatus("--color-gray-4", "No Activity", row)],
                            [null, () => this.getStatus("--color-gray-4", "No Activity", row)],
                            [RunStatus.waiting, () => this.getStatus("teal", "Waiting", row)],
                            [RunStatus.queuing, () => this.getStatus("--color-gray-4", "Queuing", row)],
                            [RunStatus.starting, () => this.getStatus("--color-purple", "Starting", row)],
                            [RunStatus.success, () => this.getStatus("--color-status-blue", "Success", row)],
                            [RunStatus.stopping, () => this.getStatus("--color-purple", "Stopping", row)],
                            [RunStatus.stopped, () => this.getStatus("--color-gray-4", "Stopped", row)],
                            [RunStatus.failure, () => this.getStatus("--color-status-red", "Failure", row)],
                            [RunStatus.failed, () => this.getStatus("--color-status-red", "Failed", row)],
                            [RunStatus.completed, () => this.getStatus("--color-status-blue", "Completed", row)],
                            [RunStatus.running, () => this.getStatus("--color-purple", "Running", row)],
                            [RunStatus.exporting, () => this.getStatus("--color-purple", "Exporting", row)],
                        ],
                        () => this.getStatus("--color-gray-4", "No Activity", row)
                    )}`,
            },
            {
                name: "menu",
                cellStyle: { textAlign: "center", width: "20px" },
                menu: (row) => this.menu(row)
            }
        ];
        //filters
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.size > 0) {
            const filterStatus = urlParams.get('status');
            if (filterStatus) {
                this.filters["status"] = filterStatus;
            }
            
            const filterAgentIds = urlParams.get('agentIds');
            const filterAgentName = urlParams.get('agentName');
            if (filterAgentIds) {
                this.filters["agentIds"] = filterAgentIds;
            } else if (filterAgentName) {
                this.filters["agentName"] = filterAgentName;
            }
        }

        this._isLoading = true;
        try {
            // Load agent names for filtering if we don't have them yet
            if (this._agentNames.length === 0) {
                await this.loadAgentNamesAsync();
                // Convert any ID-based URL parameters to names after data is loaded
                this.convertUrlIdsToNames();
            }

            const body = {
                pageIndex: this._pageIndex,
                recordsPerPage: this._recordsPerPage,
                sortColumn: this._sortColumn,
                sortOrder: this._sortOrder,
                searchTerm: this._searchTerm,
                filters: this.filters
            };
            const result = await this._runService.api.getOrgSchedulesAsync(body);
            if (result.isOk) {
                this._data = result.value.schedules;
                this._totalRecordCount = result.value.totalRecordCount;
            } else {
                this._toasterService.showUnexpectedError(result.err.message);
            }
        } finally {
            this._isLoading = false;
        }
        if (this._selectAll) this._selectAll.value = false;
    }

    private async loadAgentNamesAsync() {
        try {
            const result = await this._runService.api.getOrgScheduleAgentNamesAsync();
            
            if (result.isOk) {
                const agents = result.value;
                this._agentNames = agents
                    .filter(agent => agent.agentName)
                    .map(agent => ({
                        id: agent.configId,
                        name: agent.agentName
                    }));
            } else {
                console.error('Failed to load agents for filtering:', result.err?.message || 'Unknown error');
                this._toasterService.showError('Failed to load agent filter options. Please refresh the page.');
                this._agentNames = [];
            }
        } catch (error) {
            console.error('Error loading agents for filtering:', error);
            this._toasterService.showError('Failed to load agent filter options. Please refresh the page.');
            this._agentNames = [];
        }
    }

    private convertUrlIdsToNames() {
        // Convert agent IDs to names
        if (this.filters["agentIds"]) {
            const agentIds = this.filters["agentIds"].split(',').map(id => parseInt(id));
            const agentNames = agentIds.map(id => {
                const agent = this._agentNames.find(a => a.id === id);
                return agent?.name;
            }).filter(name => name);
            this.filters["agentName"] = agentNames.join(',');
        }
    }

    private openSpace(row: AgentTasksViewModel) {
        Router.go(`/space/${row.spaceId}`);
        this._userState.selectedSpaceId = row.spaceId;
        this._userState.selectedLabelId = -1;
        this._userState.selectedSpaceOrLabelChanged.triggerVoid();
    }


    private openAgent(row: AgentTasksViewModel) {
        Router.go(`/space/${row.spaceId}/config/${row.configId}/details/info`);
        this._userState.selectedSpaceId = row.spaceId;
        this._userState.selectedLabelId = -1;
        this._userState.selectedSpaceOrLabelChanged.triggerVoid();
    }

    private openSchedule(row: AgentTasksViewModel) {
        Router.go(`/space/${row.spaceId}/config/${row.configId}/schedule/${row.id}`);
        this._userState.selectedSpaceId = row.spaceId;
        this._userState.selectedLabelId = -1;
        this._userState.selectedSpaceOrLabelChanged.triggerVoid();
    }


    private openRunDetails(row: AgentTasksViewModel) {
        if (row.lastRunId) {
            //if negative, it's a run history id
            if (row.lastRunId < 0) {
                Router.go(`/space/${row.spaceId}/config/${row.configId}/details/run-history-details/run/${-row.lastRunId}`);
            } else {
                Router.go(`/space/${row.spaceId}/config/${row.configId}/details/run-details/run/${row.lastRunId}`);
            }
            this._userState.selectedSpaceId = row.spaceId;
            this._userState.selectedLabelId = -1;
            this._userState.selectedSpaceOrLabelChanged.triggerVoid();
        }
    }

    private editScheduleAsync(row: AgentTasksViewModel) {
        Router.go(`/space/${row.spaceId}/config/${row.configId}/schedule/${row.id}`);
        this._userState.selectedSpaceId = row.spaceId;
        this._userState.selectedLabelId = -1;
        this._userState.selectedSpaceOrLabelChanged.triggerVoid();
    }


    private refresh() {
        this.loadDataAsync();
    }

    private filterByAgentOrTaskName(evt: any) {
        // Clear the previous timer
        if (this._searchDebounceTimer) {
            clearTimeout(this._searchDebounceTimer);
        }

        this._searchDebounceTimer = setTimeout(() => {
            this._searchTerm = this._filterEditor.liveValue;
            this._searchTerm == "" ? (this._searchTerm = "null") : this._searchTerm;
            this.loadDataAsync();
        }, 500) as any;
    }

    private menu(row: AgentTasksViewModel): MenuItem[] {
        const isDisabled = row.scheduleType === 0;
        return [
            {
                text: "Edit",
                action: this.editScheduleAsync.bind(this, row)
            },
            {
                text: "Disable",
                action: this.disableScheduleAsync.bind(this, row),
                hidden: isDisabled
            },
            {
                text: "Delete",
                action: this.deleteScheduleAsync.bind(this, row)
            }
        ];
    }

    private async deleteScheduleAsync(row: AgentTasksViewModel) {
        const result = await this._modalService.openConfirmDialogAsync({
            title: "Delete",
            body: `Are you sure you want to delete schedule "${row.name}"?`,
            saveCaption: "Delete",
        });
        if (result.isSave) {
            const result = await this._runService.api.deleteTaskAsync(row.configId, row.id);
            if (result.isOk) {
                const index = this._data.indexOf(row);
                this._data.splice(index, 1);
                this._dataGrid.requestUpdate();
            } else {
                this._toasterService.showNetworkError(result.err);
            }
        }
    }

    private async disableScheduleAsync(row: AgentTasksViewModel) {
        const result = await this._modalService.openConfirmDialogAsync({
            title: "Disable Schedule",
            body: `Are you sure you want to disable the schedule named "${row.name}"?`,
            saveCaption: "Disable",
        });
        if (result.isSave) {
            const disableModel = {
                id: row.id,
                configId: row.configId,
                parallelism: 1,
                parallelMaxConcurrency: null,
                parallelExport: null,
                serverGroupId: null,
                proxyPoolId: null,
                serverOrganizationId: null,
                parameters: "{}",
                isSaveAsTask: true,
                taskName: row.name,
                isExclusive: true,
                isWaitOnFailure: false,
                schedule: "",
                localSchedule: "",
                scheduleType: 0, // disable the schedule
                startTime: row.startTime ? new Date(row.startTime) : new Date(),
                logLevel: null,
                logMode: null,
            };

            const updateResult = await this._runService.api.updateTaskAsync(row.id, disableModel);
            if (updateResult.isOk) {
                this.loadDataAsync();
                this._toasterService.showSuccess("Schedule disabled successfully");
            } else if (updateResult.isErr) {
                this._toasterService.showNetworkError(updateResult.err);
            }
        }
    }

    private async onDeleteManyAsync(event: MouseEvent) {
        event.stopPropagation();

        const count = this._dataGrid.selectedRows.length;
        const result = await this._modalService.openConfirmDialogAsync({
            title: "Delete Schedules",
            body: `Are you sure you want to delete ${count === 1 ? `1 schedule` : `${count} schedules`}?`,
            saveCaption: "Delete",
        });
        if (result.isSave) {
            const schedulesByConfigId = {};
            this._dataGrid.selectedRows.forEach((p) => {
                if (schedulesByConfigId[p.configId] === undefined) {
                    schedulesByConfigId[p.configId] = [];
                }
                schedulesByConfigId[p.configId].push(p.id);
            });

            for (const configId in schedulesByConfigId) {
                const taskIds = schedulesByConfigId[configId];
                const result = await this._runService.api.deleteTaskManyAsync(parseInt(configId), taskIds);
                if (result.isOk) {
                    this._selectAll.value = false;
                    this._hasSelectedRows = false;
                    this._data = this._data.filter((p) => !taskIds.includes(p.id));
                } else {
                    this._toasterService.showNetworkError(result.err);
                }
            }
        }
    }

    private selectAll(evt: Event) {
        if (evt.target instanceof CheckboxEditorElement) {
            if (evt.target.liveValue) {
                this._dataGrid.selectAllRows();
                this._hasSelectedRows = true;
            } else {
                this._dataGrid.clearSelection();
                this._hasSelectedRows = false;
            }
        }
    }

    private onGridSelectionChanged(evt: Event) {
        evt.stopPropagation();
        this.gridSelectionChanged();
    }

    private gridSelectionChanged() {
        if (this._dataGrid.selectedRows.length === 0) {
            this._selectAll.value = false;
            this._hasSelectedRows = false;
        } else if (this._dataGrid.selectedRows.length !== this._data.length) {
            this._selectAll.value = undefined;
            this._hasSelectedRows = true;
        } else {
            this._selectAll.value = true;
            this._hasSelectedRows = true;
        }
    }

    private sortDataGrid(evt: CustomEvent) {
        evt.stopPropagation();
        this._sortColumn = evt.detail.sortColumn;
        this._sortOrder = evt.detail.sortOrder == SortOrder.Ascending ? 1 : 0;
        this.loadDataAsync();
    }

    private onPageChanged(evt: CustomEvent) {
        evt.stopPropagation();
        this._pageIndex = evt.detail.pageIndex;
        this._dataGrid.pageIndex = this._pageIndex;
        this.loadDataAsync();
    }

    // Added methods for status filtering
    private runStatusOptions() {
        const selectedStatuses = this.filters["status"]?.split(',').map(Number) || [];
        const opts = [
            { id: -1, name: "No Activity", value: selectedStatuses.includes(-1) },
            ...Object.keys(RunStatus)
                .filter(key => isNaN(Number(key)))
                .map(key => ({
                    id: RunStatus[key as keyof typeof RunStatus],
                    name: key.charAt(0).toUpperCase() + key.slice(1),
                    value: selectedStatuses.includes(RunStatus[key as keyof typeof RunStatus])
                }))
                .sort((a, b) => a.name.localeCompare(b.name))
        ];
        return opts;
    }

    private handleStatusChange(load = true) {
        const selectedOptions: number[] = this._multiSelectStatus.selectedValues;
        this.filters["status"] = selectedOptions.join(',');
        this._pageIndex = 1;
        this._dataGrid.pageIndex = 1;
        this.updateURLQueryParam("status", this.filters["status"]);
        if (load) this.loadDataAsync();
    }

    private handleSelectAllStatus(load = true) {
        const selectedOptions: number[] = [-1, ...Object.keys(RunStatus)
            .filter(key => isNaN(Number(key)))
            .map(key => RunStatus[key as keyof typeof RunStatus])];
        this.filters["status"] = selectedOptions.join(',');
        this.updateURLQueryParam("status", this.filters["status"]);
        if (load) this.loadDataAsync();
    }

    private handleClearAllStatus(load = true) {
        const selectedOptions: number[] = [];
        this.filters["status"] = selectedOptions.join(',');
        this.updateURLQueryParam("status", this.filters["status"]);
        if (load) this.loadDataAsync();
    }

    private updateURLQueryParam(key: string, value: string) {
        const url = new URL(window.location.href);
        if (value) {
            url.searchParams.set(key, value);
        } else {
            url.searchParams.delete(key);
        }
        window.history.pushState(null, '', url.toString());
    }

    // agent name filtering methods
    private runAgentNameOptions() {
        const selectedAgentNames = this.filters["agentName"]?.split(',') || [];
        const opts = this._agentNames.map((agent) => ({
            id: agent.id,
            name: agent.name,
            value: selectedAgentNames.includes(agent.name),
        }));
        return opts;
    }

    private handleAgentNameChange(load = true) {
        const selectedOptions: number[] = this._multiSelectAgentName.selectedValues;
        const selectedAgentNames = selectedOptions.map(id => this._agentNames.find(agent => agent.id === id)?.name).filter(name => name);
        this.filters["agentName"] = selectedAgentNames.join(',');
        this.filters["agentIds"] = selectedOptions.join(',');
        this._pageIndex = 1;
        this._dataGrid.pageIndex = 1;
        this.updateURLQueryParam("agentIds", this.filters["agentIds"]);
        this.updateURLQueryParam("agentName", "");
        if (load) this.loadDataAsync();
    }

    private handleSelectAllAgentName(load = true) {
        const selectedAgentNames = this._agentNames.map(agent => agent.name);
        const selectedAgentIds = this._agentNames.map(agent => agent.id);
        this.filters["agentName"] = selectedAgentNames.join(',');
        this.filters["agentIds"] = selectedAgentIds.join(',');
        this.updateURLQueryParam("agentIds", this.filters["agentIds"]);
        this.updateURLQueryParam("agentName", "");
        if (load) this.loadDataAsync();
    }

    private handleClearAllAgentName(load = true) {
        const selectedAgentNames: string[] = [];
        const selectedAgentIds: string[] = [];
        this.filters["agentName"] = selectedAgentNames.join(',');
        this.filters["agentIds"] = selectedAgentIds.join(',');
        this.updateURLQueryParam("agentIds", this.filters["agentIds"]);
        this.updateURLQueryParam("agentName", "");
        if (load) this.loadDataAsync();
    }

    render() {
        const selectTitle = this._hasSelectedRows ? "Clear Selection" : "Select All";
        return html`
            <div class="body">
                <div class="title"><h1>Organization Schedules</h1></div>
                <div class="header">
                    <div class="left-header">
                        <se-checkbox-editor
                            style="margin-right:5px"
                            id="selectAll"
                            tristate="auto"
                            @valueChanged=${this.selectAll}
                            ${htmlTitle(selectTitle)}
                            .value=${false}
                            .disabled=${(this._data?.length ?? 0) === 0}
                        ></se-checkbox-editor>

                        ${this._hasSelectedRows
                            ? html`
                                  <se-secondary-button
                                      @mousedown=${(event) => this.onDeleteManyAsync(event)}
                                      ${htmlTitle("Delete selected schedules")}
                                      .customStyle=${{ padding: "1px 6px", margin: 0 }}
                                      icon="far fa-trash-alt"
                                  ></se-secondary-button>
                              `
                            : html`
                                  <se-secondary-button
                                      @click=${this.refresh}
                                      ${htmlTitle("Refresh")}
                                      .customStyle=${{ padding: "1px 6px", margin: 0 }}
                                      icon="far fa-redo"
                                  ></se-secondary-button>
                              `}
                        ${!this._hasSelectedRows ? html`
                             <se-multi-select-editor 
                                id="multiSelectStatus"
                                @valueChanged=${this.handleStatusChange}
                                @selectAll=${this.handleSelectAllStatus}
                                @clearAll=${this.handleClearAllStatus}
                                .isSelectAll=${true}
                                .optionsName=${"Status"}
                                .options=${this.runStatusOptions()}>
                             </se-multi-select-editor>
                             <se-multi-select-editor 
                                id="multiSelectAgentName"
                                @valueChanged=${this.handleAgentNameChange}
                                @selectAll=${this.handleSelectAllAgentName}
                                @clearAll=${this.handleClearAllAgentName}
                                .isSelectAll=${true}
                                .enableSearch=${true}
                                .optionsName=${"Agent"}
                                .options=${this.runAgentNameOptions()}>
                             </se-multi-select-editor>
                        ` : html``}
                        <se-input-editor
                            id="filter"
                            name="filter"
                            type="text"
                            label="Filter&nbsp;by&nbsp;Name"
                            labelPosition="left"
                            gap="5px"
                            input-type="text"
                            size="30"
                            @editorChanged=${this.filterByAgentOrTaskName}
                        ></se-input-editor>
                    </div>
                </div>

                <se-data-grid
                    class="grid"
                    .rows=${this._data}
                    .columns=${this._columns}
                    selectable
                    @selectionchanged=${this.onGridSelectionChanged}
                    @sortdata=${this.sortDataGrid}
                    placeholder="No scheduled runs available."
                    .isLoading=${this._isLoading}
                    .pageIndex=${this._pageIndex}
                    .syncWithUrl=${true}
                ></se-data-grid>
                <se-pagination
                    .recordCount=${this._totalRecordCount}
                    .recordsPerPage=${this._recordsPerPage}
                    @pagechanged=${this.onPageChanged}
                    .pageIndex=${this._pageIndex}
                    .dataType=${"Schedules"}
                    .syncWithUrl=${true}
                ></se-pagination>
            </div>
        `;
    }

    static styles = css`
        :host {
            display: block;
            box-sizing: border-box;
            font: var(--font);
            height: 100%;
            padding: 15px;
        }
        .body {
            min-height: 220px;
            height: 100%;
            display: flex;
            flex-direction: column;
            gap: 5px;
        }
        h1 {
            margin: 0px;
            font-weight: 600;
        }
        .header {
            margin-left: 5px;
            display: flex;
            align-items: end;
            justify-content: space-between;
            padding-right: 5px;
            margin-right: -5px;
            padding-bottom: 5px;
            margin-bottom: -5px;
        }
        .left-header {
            display: flex;
            align-items: center;
            gap: 5px;
        }
        .grid {
            flex: 1;
        }
    `;
}